import {
    useCallback,
    useMemo,
} from 'react'

import FormWithControls from 'app/shared-components/FormWithControls'
import useEnumValues from 'app/hooks/useEnumValues'
import {
    InvoiceDesignation,
} from 'app/enums/label.enums'
import useGetLongTermLeaseInvoices from 'app/requests/LongTermLease/hooks/useGetLongTermLeaseInvoices'
import requestConfig from 'app/requests/LongTermLeaseInvoice/LongTermLeaseInvoice.requests'
import {
    LongTermLeaseInvoiceType,
} from 'app/requests/LongTermLeaseInvoice/LongTermLeaseInvoice.types'

import getInvoiceFields from './invoiceForm.fields'
import useStyles from './invoiceForm.styles'

const InvoiceForm = ({
    longTermLeaseInvoice,
    onSuccess,
    longTermLeaseId,
}: Readonly<{
    longTermLeaseInvoice: LongTermLeaseInvoiceType,
    onSuccess: () => void,
    longTermLeaseId: number,
}>) => {
    const {
        classes,
    } = useStyles()

    const getAllowedValues = useEnumValues()

    const {
        data: longTermLeaseInvoices,
    } = useGetLongTermLeaseInvoices({
        longTermLeaseId,
    })

    const originalInvoiceAllowedValues = useMemo(() => {
        return longTermLeaseInvoices?.reduce((acc, element) => {
            if (element.invoiceDesignation !== InvoiceDesignation.CREDIT_NOTE) {
                const additionalInfo = element?.sapSalesOrderId || ''

                return {
                    ...acc,
                    [element.id]: `${element.invoiceTitle}${additionalInfo ? ', ' : ''}${additionalInfo}`,
                }
            }
            return acc
        }, {})
    }, [longTermLeaseInvoices])

    const fields = useMemo(() => {
        return getInvoiceFields({
            getAllowedValues,
            originalInvoiceAllowedValues,
            isCreditNote: longTermLeaseInvoice?.invoiceDesignation
                === InvoiceDesignation.CREDIT_NOTE,
        })
    }, [
        getAllowedValues,
        longTermLeaseInvoice?.invoiceDesignation,
        originalInvoiceAllowedValues,
    ])

    const requestParams = useMemo(() => {
        return requestConfig.updateLongTermLeaseInvoice({
            longTermLeaseInvoiceId: longTermLeaseInvoice?.id,
        })
    }, [longTermLeaseInvoice?.id])

    const modifyDataBeforeSend = useCallback(
        (requestData: LongTermLeaseInvoiceType): Partial<LongTermLeaseInvoiceType> => {
            return {
                invoiceDate: requestData.invoiceDate,
                invoiceType: requestData.invoiceType,
                billedMonth: requestData.billedMonth,
                billedYear: requestData.billedYear,
                invoiceComment: requestData.invoiceComment,
                creditNoteOriginalInvoice: requestData.creditNoteOriginalInvoice,
                creditNoteReason: requestData.creditNoteReason,
            }
        }, [],
    )

    return (
        <FormWithControls
            name="LongTermLeaseInvoiceView"
            value={longTermLeaseInvoice}
            fields={fields}
            onSuccess={onSuccess}
            modifyDataBeforeSend={modifyDataBeforeSend}
            classNames={{
                gridWrapper: classes.gridWrapper,
            }}
            requestParams={requestParams}
            exists
        />
    )
}

export default InvoiceForm
